// ============================================================================
// ENGAGEMENT TYPE HOOKS
// ============================================================================
// React hooks for the EngagementType master.
//
// Pattern follows useSampleCategories.ts:
// - useEngagementTypes(params)            → list with pagination, refetch
// - useEngagementType(id)                 → single record by id
// - useCreateEngagementType()             → create mutation
// - useUpdateEngagementType()             → update mutation
// - useDeleteEngagementType()             → delete mutation
//
// Note: useEngagementTypes serializes params via JSON.stringify in the dep
// array to avoid infinite re-render when callers pass an inline object.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { engagementTypeService } from 'api/services/engagementTypeService';
import type {
  ID,
  PaginatedResponse,
  EngagementType,
  CreateEngagementTypeRequest,
  UpdateEngagementTypeRequest,
  EngagementTypeSearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch a paginated list of engagement types.
 * Refetches automatically when `params` changes.
 */
export const useEngagementTypes = (params?: EngagementTypeSearchRequest) => {
  const [data, setData] = useState<EngagementType[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<EngagementType>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Serialize params so a new object reference each render doesn't re-fire the effect
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchEngagementTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await engagementTypeService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchEngagementTypes();
  }, [fetchEngagementTypes]);

  return {
    data,
    pagination,
    loading,
    error,
    refetch: fetchEngagementTypes,
  };
};

/**
 * Hook to fetch a single engagement type by id.
 */
export const useEngagementType = (id: ID | null | undefined) => {
  const [data, setData] = useState<EngagementType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchEngagementType = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await engagementTypeService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEngagementType();
  }, [fetchEngagementType]);

  return { data, loading, error, refetch: fetchEngagementType };
};

/**
 * Hook to create a new engagement type.
 */
export const useCreateEngagementType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createEngagementType = async (
    data: CreateEngagementTypeRequest,
    options?: {
      onSuccess?: (item: EngagementType) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await engagementTypeService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createEngagementType, loading, error, success };
};

/**
 * Hook to update an engagement type. Always pass the latest rowVersion.
 */
export const useUpdateEngagementType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateEngagementType = async (
    id: ID,
    data: UpdateEngagementTypeRequest,
    options?: {
      onSuccess?: (item: EngagementType) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await engagementTypeService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateEngagementType, loading, error, success };
};

/**
 * Hook to delete an engagement type (soft-delete on the server).
 */
export const useDeleteEngagementType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteEngagementType = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await engagementTypeService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteEngagementType, loading, error, success };
};
